import './app-settings.scss';

import React, {
  FunctionComponent,
  useCallback,
  useReducer,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Alert from '@justgiving/alert';
import Loading from '@justgiving/loading';
import Modal from '@justgiving/modal';
import config from '../../config';
import disconnectFacebookThunk from '../../actions/app-settings/disconnectFacebook.thunk';
import { useTranslation } from 'react-i18next';

type Props = {
  className?: string;
};
type SelectedState = {
  HasFacebookCredentials: boolean;
  HasJustGivingCredentials: boolean;
};
type ComponentState = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
};

const FacebookSettings: FunctionComponent<Props> = ({ className = '' }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { HasFacebookCredentials, HasJustGivingCredentials } = useSelector<
    State,
    SelectedState
  >(state => ({
    HasFacebookCredentials: state.app.user.data.HasFacebookCredentials,
    HasJustGivingCredentials: state.app.user.data.HasJustGivingCredentials,
  }));
  const [isModalOpen, setIsModalOpen] = useState(false);

  const initialState = {
    isLoading: false,
    isError: false,
    isSuccess: false,
  };
  const reducer = (state: ComponentState, action: { type: string }) => {
    switch (action.type) {
      case 'DISCONNECTING':
        return { isSuccess: false, isLoading: true, isError: false };
      case 'DISCONNECTING_SUCCESS':
        return { isError: false, isLoading: false, isSuccess: true };
      case 'DISCONNECTING_FAILURE':
        return {
          isLoading: false,
          isSuccess: false,
          isError: true,
        };
      default:
        return state;
    }
  };

  const [{ isLoading, isError, isSuccess }, dispatchState] = useReducer(
    reducer,
    initialState,
  );

  const openModal = useCallback(() => setIsModalOpen(true), [setIsModalOpen]);
  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen]);
  const disconnectFacebook = useCallback(async () => {
    try {
      dispatchState({ type: 'DISCONNECTING' });
      await dispatch(disconnectFacebookThunk());
      dispatchState({ type: 'DISCONNECTING_SUCCESS' });
    } catch {
      dispatchState({ type: 'DISCONNECTING_FAILURE' });
    }
  }, [dispatch, dispatchState]);

  if (isSuccess) {
    return (
      <Alert displayType="inline" type="success" className="jg-space-mvlg">
        {t('FacebookSettings.success')}
      </Alert>
    );
  }

  if (!(HasFacebookCredentials && HasJustGivingCredentials)) return null;

  return (
    <>
      <section className={`app-settings ${className}`}>
        <h2 className="jg-h4">Facebook</h2>
        <div className="app-settings__row">
          <div className="app-settings__logo">
            <img
              src={`${config.publicPath}Facebook.svg`}
              width="48"
              height="48"
              alt="Facebook logo"
            />
          </div>
          <div className="app-settings__content">
            {t('FacebookSettings.description')}
            <button
              className="app-settings__button jg-text--link"
              onClick={openModal}
              type="button"
            >
              {t('FacebookSettings.ButtonLabel')}
            </button>
          </div>
        </div>
      </section>
      <Modal isOpen={isModalOpen} onRequestClose={closeModal}>
        <div className="app-settings__modal">
          <h2 className="jg-text--center app-settings__modal__title ">
            {t('FacebookSettings.ModalHeading')}
          </h2>
          <div className="jg-text--center jg-space-mvlg app-settings__modal__description">
            <p>{t('FacebookSettings.ModalDescription')}</p>
          </div>
          <div className="app-settings__modal__buttons">
            {isError && (
              <Alert
                displayType="inline"
                type="warning"
                className="jg-space-mbmd"
              >
                {t('FacebookSettings.failure')}
              </Alert>
            )}
            <button
              onClick={disconnectFacebook}
              className="jg-btn jg-btn--brand jg-space-mbsm"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loading size="small" colour="white" />
              ) : (
                t('FacebookSettings.ModalButtonLabel')
              )}
            </button>
            <button onClick={closeModal} className="jg-btn jg-btn--ghost">
              {t('FacebookSettings.ButtonNotNow')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default FacebookSettings;
